import React from 'react';
import {
  Box,
  Divider,
  Text,
  TextButton,
  Heading,
  Breadcrumbs,
} from '@wix/design-system';
import {
  useEssentials,
  useJunkYard,
  useRouterContext,
  useServices,
  useSiteContext,
  QueryDecorator,
  AppMarketPage,
  getPageViewBiData,
  Route,
  usePageLoad,
} from '@wix/app-market-core';
import { RoutePath } from '@wix/app-market-core-types';
import {
  appMarketBackButtonClick,
  appMarketCategoryClickSrc24Evid185,
  appMarketPageView,
} from '@wix/bi-logger-app-market-data/v2';
import { getSubCategoryHeaderPageData } from './sub-category-page-data';
import type { SubCategoryPageHeaderData } from './sub-category-page-data';
import { SubCategoryHeaderSkeleton } from './sub-category-header-skeleton';
import { BackButton } from '../../components/back-button';
import { useQueryClient } from '@tanstack/react-query';

function SubCategoryPageHeaderComponent({
  slug,
  parentSlug,
  parentName,
  primaryTitle: title,
}: SubCategoryPageHeaderData) {
  const { route } = useRouterContext();
  const { routerData } = useJunkYard();
  const { baseURL, horizontalNav } = useSiteContext();
  const { biLogger } = useEssentials();
  const { router } = useRouterContext();
  const page = AppMarketPage.SUB_CATEGORY;

  // PageView reports from PageHeader because its required title and parentName.
  usePageLoad({
    route,
    oldRoute: routerData.route,
    onLoad: () =>
      biLogger.report(
        appMarketPageView(
          getPageViewBiData({
            routerData,
            pageType: AppMarketPage.SUB_CATEGORY,
            pageName: parentSlug,
            subCategories: [slug],
          }),
        ),
      ),
  });

  function onBackClick() {
    biLogger.report(
      appMarketBackButtonClick({
        tag_type: page,
        tag_name: slug,
        location: 'sub_category_page',
      }),
    );
    router.navigateTo({
      path: RoutePath.BACK,
      payload: undefined,
    });
  }

  function getCategoryHref(categorySlug: string) {
    const hasHref = router.hasHref();
    if (!hasHref) {
      return undefined;
    }
    const route = new Route({
      path: RoutePath.CATEGORY,
      payload: {
        slug: categorySlug,
      },
      baseURL,
    });
    return route.toHref();
  }

  function onCategoryButtonClick(categorySlug: string) {
    biLogger.report(
      appMarketCategoryClickSrc24Evid185({
        tag_type: 'category',
        tag_name: categorySlug,
        sub_tag_name: slug,
      }),
    );
    router.navigateTo({
      path: RoutePath.CATEGORY,
      payload: {
        slug: categorySlug,
      },
    });
  }

  if (horizontalNav) {
    return (
      <Box gap="12px" verticalAlign="middle">
        {router.hasHistory() && (
          <BackButton
            onBack={onBackClick}
            size="small"
            weight="thin"
            skin="dark"
          />
        )}
        <Heading size="medium" dataHook="sub-category-page-title">
          {title}
        </Heading>
      </Box>
    );
  }

  return (
    <>
      <Box dataHook="breadcrumbs" marginTop="24px">
        {router.hasHistory() && (
          <>
            <Box verticalAlign="middle">
              <BackButton
                onBack={onBackClick}
                size="small"
                weight="thin"
                skin="dark"
              />
            </Box>
            <Box margin="0px 12px 0px 18px" height="12px" alignSelf="center">
              <Divider direction="vertical" />
            </Box>
          </>
        )}
        <Breadcrumbs
          items={[
            {
              id: 0,
              value: 'Category',
              customElement: (
                <TextButton
                  dataHook="category-parent-button"
                  size="small"
                  weight="normal"
                  as="a"
                  href={getCategoryHref(parentSlug)}
                  onClick={(e) => {
                    onCategoryButtonClick(parentSlug);
                    e.preventDefault();
                  }}
                >
                  {parentName}
                </TextButton>
              ),
            },
            {
              id: 1,
              value: title,
              customElement: (
                <Text weight="thin" size="small" secondary disabled>
                  {title}
                </Text>
              ),
            },
          ]}
          size="medium"
        />
      </Box>
      <Box marginTop="6px">
        <Heading size="extraLarge" dataHook="sub-category-page-title">
          {title}
        </Heading>
      </Box>
    </>
  );
}

export function SubCategoryPageHeader({
  slug,
  parentSlug,
}: {
  slug: string;
  parentSlug: string;
}) {
  const { languageCode } = useSiteContext();
  const services = useServices();
  const queryClient = useQueryClient();

  return (
    <QueryDecorator
      queryFn={() =>
        getSubCategoryHeaderPageData({
          queryClient,
          slug,
          parentSlug,
          services,
          languageCode,
        })
      }
      queryKey={`sub-category-header-${languageCode}-${slug}-${parentSlug}`}
    >
      {({ data, isLoading }) =>
        isLoading ? (
          <SubCategoryHeaderSkeleton />
        ) : (
          <SubCategoryPageHeaderComponent {...data} />
        )
      }
    </QueryDecorator>
  );
}
